import type { LiveFixture } from '../../api/football'
import type { SimGameStatus } from '../../gameweekSimulation'
import type { Game } from '../../types'

/**
 * One live state per fixture, resolved in the grid rather than per card.
 *
 * There are two possible sources and they are not equal:
 *
 * - the **server snapshot** from `/api/live`, which is what every user watching the same match is
 *   looking at, and
 * - the **client-side mock playback** (`useGameweekSimulation`), which is one browser animating a
 *   bundled result set for itself.
 *
 * The snapshot wins wherever it has an opinion about a fixture — including the opinion "this one
 * is not in play" — and the mock sim is the fallback for fixtures it says nothing about. That
 * leaves mock mode, where the snapshot is always empty, behaving exactly as it did before.
 */

/** The only two statuses that mean a match is being played right now. `postponed`, `abandoned`,
 *  `cancelled` and `unknown` are emphatically not in-play, and neither is `finished` — full time
 *  belongs to the results store and the reveal cascade. */
const IN_PLAY_STATUSES = new Set<LiveFixture['status']>(['live', 'halftime'])

export function isInPlay(status: LiveFixture['status']): boolean {
  return IN_PLAY_STATUSES.has(status)
}

export interface CardLiveState {
  /**
   * The server's status for this fixture, or `null` when the state came from the mock sim — which
   * has no status and no minute, so its badge stays the plain "LIVE" it has always been.
   */
  status: 'live' | 'halftime' | null
  /** The provider's minute at the last poll. `null` when it reports none — never fabricated. */
  minute: number | null
  /** The live scoreline. Absent until both sides of it exist. */
  result?: { homeScore: number; awayScore: number }
}

export interface ResolveLiveStatesArgs {
  games: Game[]
  /** In-play fixtures as of the last poll (`useLivePoll`). Empty in mock mode. */
  liveFixtures?: LiveFixture[]
  simGameStatuses?: Record<string, SimGameStatus>
  simLiveResults?: Record<string, { homeScore: number; awayScore: number }>
}

export function resolveLiveStates({
  games,
  liveFixtures,
  simGameStatuses,
  simLiveResults,
}: ResolveLiveStatesArgs): Record<string, CardLiveState> {
  const snapshot = new Map((liveFixtures ?? []).map((fixture) => [fixture.id, fixture]))
  const states: Record<string, CardLiveState> = {}

  for (const game of games) {
    const fixture = snapshot.get(game.id)
    if (fixture) {
      // The snapshot has an opinion: honour it, in-play or not. A fixture the server has moved on
      // from must not fall back to a mock sim still animating it.
      if (!isInPlay(fixture.status)) continue
      states[game.id] = {
        status: fixture.status as 'live' | 'halftime',
        minute: fixture.minute,
        result:
          fixture.homeScore !== null && fixture.awayScore !== null
            ? { homeScore: fixture.homeScore, awayScore: fixture.awayScore }
            : undefined,
      }
      continue
    }
    if (simGameStatuses?.[game.id] === 'live') {
      states[game.id] = { status: null, minute: null, result: simLiveResults?.[game.id] }
    }
  }

  return states
}
